import { createContext, use, useContext, useState } from "react";
import { config } from "@/config/env";
import { AppConfigContext } from "@/contexts/AppConfigContext";
import { logError } from "@/utils/logError";

export type FeatureFlags = { demoMode: boolean };

const STORAGE_KEY = "campnerd_feature_flags";

function readFromStorage(): FeatureFlags {
  const flagDefaults: FeatureFlags = { demoMode: config.demoMode };
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const storedValues = JSON.parse(stored) as Record<string, unknown>;
      const merged = {
        demoMode:
          typeof storedValues.demoMode === "boolean" ? storedValues.demoMode : config.demoMode,
      };
      const sanitized = JSON.stringify(merged);
      if (sanitized !== stored) localStorage.setItem(STORAGE_KEY, sanitized);
      return merged;
    }
  } catch (error) {
    logError(error);
  }
  return flagDefaults;
}

const FeaturesContext = createContext<
  | {
      flags: FeatureFlags;
      setFlag: <K extends keyof FeatureFlags>(key: K, value: FeatureFlags[K]) => void;
      panelOpen: boolean;
      setPanelOpen: (open: boolean) => void;
    }
  | undefined
>(undefined);

export function FeaturesProvider({ children }: { children: React.ReactNode }) {
  const demoMode = use(AppConfigContext)?.demoMode;
  const [flags, setFlags] = useState<FeatureFlags>(() => {
    const stored = readFromStorage();
    if (typeof demoMode === "boolean") return { ...stored, demoMode };
    return stored;
  });
  const [panelOpen, setPanelOpen] = useState(false);

  function setFlag<K extends keyof FeatureFlags>(key: K, value: FeatureFlags[K]) {
    const next = { ...flags, [key]: value } as FeatureFlags;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (error) {
      logError(error);
    }
    setFlags(next);
  }

  return (
    <FeaturesContext.Provider value={{ flags, setFlag, panelOpen, setPanelOpen }}>
      {children}
    </FeaturesContext.Provider>
  );
}

export function useFeatureFlags() {
  const context = useContext(FeaturesContext);
  if (!context) {
    throw new Error("useFeatureFlags must be used within FeaturesProvider");
  }
  return context;
}
